import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { consumeClassPhotoTrialWelcome } from '../../utils/classPhotoTrialWelcome';
import {
  HWP_DOC_DOWNLOAD_REQUEST_EVENT,
  readHwpDocLastResult,
  resolveHwpDocContinue,
} from '../../utils/hwpDocContinue';
import Dialog from '../common/Dialog';
import ModalActions from '../common/ModalActions';
import './ClassPhotoTrialWelcome.css';

const FEATURES = ['PPT', '학습지', 'AI한글', '생기부', 'AI 채점'];

/**
 * 가입 복귀 후 7일 PRO 환영 모달.
 * 한글 파일 이벤트(hwp_doc) 트리거일 때만 버튼이 바뀐다:
 *   주 = 시험지/안내문은 "이어서 편집하기", 생기부(record)는 "AI 생기부 생성에서 이어서 쓰기".
 *   부 = "한글 파일 저장"(텍스트 결과면 "결과 보기").
 * 다른 이벤트(우리반 프사·놀이 등)는 기존 버튼 그대로.
 */
const ClassPhotoTrialWelcome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchSubscriptionStatus } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [hwpDoc, setHwpDoc] = useState(null); // { type, result, fields } | null

  useEffect(() => {
    const welcome = consumeClassPhotoTrialWelcome();
    if (!welcome) return;
    if (String(welcome.triggerFeature || '').includes('hwp_doc')) {
      const saved = readHwpDocLastResult();
      setHwpDoc(saved ? { type: saved.type, result: saved, fields: saved.fields || null } : null);
    } else {
      setHwpDoc(null);
    }
    setIsOpen(true);
    fetchSubscriptionStatus(true);
  }, [location.key, fetchSubscriptionStatus]);

  const close = () => setIsOpen(false);

  const continueHwpDoc = () => {
    close();
    if (!hwpDoc) return;
    // 페이지가 ?continue=1 로 이미 목적지로 옮겨 놓았다면 다시 보내지 않는다.
    if (/^\/(chat|create-saenggibu)/.test(location.pathname)) return;
    const next = resolveHwpDocContinue(hwpDoc);
    if (next) navigate(next.to, next.options);
  };

  const downloadHwpDoc = () => {
    close();
    window.dispatchEvent(new CustomEvent(HWP_DOC_DOWNLOAD_REQUEST_EVENT, { detail: { docId: hwpDoc?.result?.doc_id } }));
  };

  const isRecord = hwpDoc?.type === 'record' || hwpDoc?.result?.result_kind === 'text';
  const isHwpx = hwpDoc?.result?.result_kind !== 'text';
  const actions = hwpDoc
    ? {
      primary: {
        label: isRecord ? 'AI 생기부 생성에서 이어서 쓰기' : '이어서 편집하기',
        onClick: continueHwpDoc,
        testId: 'class-photo-trial-welcome-continue',
      },
      secondary: isHwpx
        ? { label: '한글 파일 저장', onClick: downloadHwpDoc, testId: 'class-photo-trial-welcome-download' }
        : { label: '결과 보기', onClick: close },
    }
    : {
      primary: { label: '방금 하던 작업 이어가기', onClick: close },
      secondary: {
        label: 'PRO 기능 둘러보기',
        onClick: () => {
          close();
          navigate('/home');
        },
      },
    };

  return (
    <Dialog
      align="center"
      closeLabel="축하 안내 닫기"
      description={hwpDoc
        ? (isRecord
          ? '방금 만든 세특 문장을 AI 생기부 생성에서 이어서 쓸 수 있어요.'
          : '방금 만든 문서를 왓퀴즈에서 이어서 편집하거나 한글 파일로 저장할 수 있어요.')
        : '카드 등록 없이 7일 동안 제한 없이 직접 만들어보세요.'}
      eyebrow="가입을 축하해요"
      footer={<ModalActions primary={actions.primary} secondary={actions.secondary} />}
      icon={(
        <span aria-hidden="true" className="class-photo-trial-welcome__badge">PRO</span>
      )}
      onClose={close}
      open={isOpen}
      size="sm"
      testId="class-photo-trial-welcome"
      title="7일 PRO가 자동으로 시작됐어요"
    >
      <ul aria-label="추천 PRO 기능" className="class-photo-trial-welcome__features">
        {FEATURES.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
    </Dialog>
  );
};

export default ClassPhotoTrialWelcome;
